/**
 * Переиспользуемый мини-гид по экрану.
 *
 * Затемняет экран, «вырезает» подсветку вокруг целевого блока (по атрибуту
 * `data-tour="<id>"`) и показывает карточку с инструкцией. Шаги, чьи цели
 * отсутствуют в DOM (условно отрисованные блоки), молча пропускаются —
 * нумерация считается по реально видимым шагам.
 *
 * Управление состоянием (когда открыть, флаг «уже видел») — в useOnboardingTour.
 * Массив `steps` должен иметь стабильную ссылку (объявляйте на уровне модуля),
 * иначе фильтрация шагов будет сбрасывать прогресс на каждый рендер.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { X, ChevronLeft, ChevronRight } from 'lucide-react'

export interface TourStep {
  /** Значение data-tour целевого элемента. Без него — центрированная карточка без подсветки. */
  target?: string
  title: string
  body: ReactNode
}

interface OnboardingTourProps {
  steps: TourStep[]
  open: boolean
  onClose: () => void
}

const PADDING = 8 // отступ «дырки» вокруг подсвеченного элемента
const GAP = 12 // зазор между подсветкой и карточкой инструкции

export function OnboardingTour({ steps, open, onClose }: OnboardingTourProps) {
  const [activeSteps, setActiveSteps] = useState<TourStep[]>([])
  const [index, setIndex] = useState(0)
  const [rect, setRect] = useState<DOMRect | null>(null)
  const cardRef = useRef<HTMLDivElement>(null)
  const [cardSize, setCardSize] = useState({ w: 320, h: 180 })

  // Оставляем только шаги, чьи цели реально присутствуют в DOM (условные блоки пропускаем).
  useEffect(() => {
    if (!open) return
    const present = steps.filter(
      (s) => !s.target || document.querySelector(`[data-tour="${s.target}"]`)
    )
    setActiveSteps(present.length ? present : steps)
    setIndex(0)
  }, [open, steps])

  const step = activeSteps[index]

  const goNext = useCallback(() => {
    setIndex((i) => {
      if (i >= activeSteps.length - 1) {
        onClose()
        return i
      }
      return i + 1
    })
  }, [activeSteps.length, onClose])
  const goPrev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), [])

  // Измеряем цель и следим за скроллом/ресайзом, чтобы подсветка «ехала» за блоком.
  useEffect(() => {
    if (!open || !step) return
    const measure = () => {
      if (!step.target) {
        setRect(null)
        return
      }
      const el = document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null
      setRect(el ? el.getBoundingClientRect() : null)
    }
    const el = step.target
      ? (document.querySelector(`[data-tour="${step.target}"]`) as HTMLElement | null)
      : null
    el?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    measure()
    const raf = requestAnimationFrame(measure)
    const settle = setTimeout(measure, 350) // догнать после smooth-скролла
    window.addEventListener('scroll', measure, true)
    window.addEventListener('resize', measure)
    return () => {
      cancelAnimationFrame(raf)
      clearTimeout(settle)
      window.removeEventListener('scroll', measure, true)
      window.removeEventListener('resize', measure)
    }
  }, [open, step])

  // Размер карточки нужен для её позиционирования относительно подсветки.
  useLayoutEffect(() => {
    if (cardRef.current) {
      const r = cardRef.current.getBoundingClientRect()
      setCardSize({ w: r.width, h: r.height })
    }
  }, [index, activeSteps, rect])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowRight' || e.key === 'Enter') goNext()
      else if (e.key === 'ArrowLeft') goPrev()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, goNext, goPrev, onClose])

  if (!open || !step) return null

  const vw = window.innerWidth
  const vh = window.innerHeight
  const cardW = Math.min(340, vw - 24)

  // Позиция карточки: под подсветкой, если влезает, иначе над ней; без цели — по центру.
  let cardLeft = (vw - cardW) / 2
  let cardTop = (vh - cardSize.h) / 2
  if (rect) {
    cardLeft = Math.min(Math.max(rect.left + rect.width / 2 - cardW / 2, 12), vw - cardW - 12)
    const below = rect.bottom + GAP
    const above = rect.top - GAP - cardSize.h
    cardTop = below + cardSize.h <= vh - 12 || above < 12 ? below : above
    cardTop = Math.min(Math.max(cardTop, 12), vh - cardSize.h - 12)
  }

  const isLast = index >= activeSteps.length - 1
  const isFirst = index === 0

  return (
    <div className="fixed inset-0 z-[100]">
      {rect ? (
        <div
          className="pointer-events-none fixed rounded-xl ring-2 ring-primary transition-all duration-200"
          style={{
            top: rect.top - PADDING,
            left: rect.left - PADDING,
            width: rect.width + PADDING * 2,
            height: rect.height + PADDING * 2,
            boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)',
          }}
        />
      ) : (
        <div className="fixed inset-0 bg-black/55" />
      )}

      {/* Перехват кликов по фону — гид только показывает, без взаимодействия с экраном. */}
      <div className="fixed inset-0" />

      <div
        ref={cardRef}
        className="fixed z-[101] rounded-xl border border-border bg-card p-4 shadow-2xl"
        style={{ top: cardTop, left: cardLeft, width: cardW }}
      >
        <button
          onClick={onClose}
          aria-label="Закрыть подсказку"
          className="absolute right-2 top-2 rounded p-1 text-muted-foreground transition-colors hover:bg-muted"
        >
          <X className="h-4 w-4" />
        </button>
        <div className="mb-1 text-xs font-medium text-muted-foreground">
          Шаг {index + 1} из {activeSteps.length}
        </div>
        <h3 className="mb-1.5 pr-6 text-sm font-semibold text-foreground">{step.title}</h3>
        <div className="text-sm leading-snug text-muted-foreground">{step.body}</div>
        <div className="mt-4 flex items-center justify-between">
          <button onClick={onClose} className="text-xs text-muted-foreground transition-colors hover:text-foreground">
            Пропустить
          </button>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                onClick={goPrev}
                className="inline-flex items-center gap-1 rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Назад
              </button>
            )}
            <button
              onClick={goNext}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
            >
              {isLast ? 'Понятно' : 'Далее'}
              {!isLast && <ChevronRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
